import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	registerRemixCancellationSession,
	unregisterRemixCancellationSession,
} from '@atlassian/conversation-assistant-store/controllers/remix-cancellation-signals';
import {
	endRemix,
	getRemixMachine,
	sendRemixEvent,
	startRemix,
	type RemixPhase,
	useMachinePhase,
} from '@atlassian/malleable-ui-components/state/machine';
import type { RemixExplicitFlowEndReason } from '@atlassian/malleable-ui-components/state/machine/remix-command-bus';

import type { RendererRemixTerminalPhase } from './rendererRemixLifecycle.ts';

type PendingIntent = 'preview-opened' | undefined;
type ClaimedFinalization = Readonly<{
	remixId: string;
	cancellationSessionId: string | undefined;
}>;
type AdmissionRejectionReason = 'active-flow' | 'error' | 'identity-mismatch';
type LifecycleOperation =
	| 'endRemix'
	| 'notifyAdmissionRejected'
	| 'sendPreviewOpened'
	| 'sendTerminalEvent'
	| 'sendUnmountCancelled'
	| 'startRemix'
	| 'validateAdmissionIdentity';

type StartRendererRemixArgs = {
	conversationId?: string;
	dynamicUiType: string;
	dynamicUiSubtype?: string;
	source?: InteractionSource;
};

type RemixAdmission =
	| { conversationId: string; admitted: true; cancellationSessionId: string }
	| { conversationId: string; admitted: false; reason: AdmissionRejectionReason };

const readMachineTerminalPhase = (remixId: string): RendererRemixTerminalPhase | undefined => {
	const phase = getRemixMachine(remixId)?.state();
	return phase === 'aborted' || phase === 'error' ? phase : undefined;
};

export type RendererRemixMachineLifecycle = {
	phase: RemixPhase | undefined;
	/** Identifies the admitted flow. */
	remixId: string | undefined;
	/** Synchronous command ownership check for the exact admitted flow. */
	isAdmittedRemixId: (remixId: string) => boolean;
	/** Starts authoritative machine admission before the renderer sends its Rovo request. */
	startFlow: (args: StartRendererRemixArgs) => RemixAdmission;
	onPreviewOpened: () => void;
	onPreviewDismissed: () => boolean;
	onStopThinking: () => boolean;
	onRovoAborted: () => void;
	onErrorDismissed: () => void;
	onSendRejected: () => boolean;
	onNavigation: () => boolean;
};

/** Adapts renderer UI intent to the Remix state machine. */
export const useRendererRemixMachineLifecycle = ({
	onAdmissionRejected,
}: {
	onAdmissionRejected?: (reason: AdmissionRejectionReason) => void;
} = {}): RendererRemixMachineLifecycle => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const logLifecycleOperationError = useCallback(
		(operation: LifecycleOperation, error: unknown) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'rendererRemixLifecycleOperation',
					source: 'rendererRemix',
					attributes: {
						operation,
						errorName: error instanceof Error ? error.name : typeof error,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);
	const runLifecycleSafely = useCallback(
		(operationName: LifecycleOperation, operation: () => void): boolean => {
			try {
				operation();
				return true;
			} catch (error) {
				logLifecycleOperationError(operationName, error);
				return false;
			}
		},
		[logLifecycleOperationError],
	);

	const [remixId, setRemixId] = useState<string>();
	const phase = useMachinePhase(remixId ?? '');
	const phaseRef = useRef(phase);
	phaseRef.current = phase;
	const remixIdRef = useRef<string | undefined>(undefined);
	const commandRemixIdRef = useRef<string | undefined>(undefined);
	const cancellationSessionIdRef = useRef<string | undefined>(undefined);
	const pendingIntentRef = useRef<PendingIntent>(undefined);
	const finalizedRef = useRef(true);
	const claimFinalization = useCallback((): ClaimedFinalization | undefined => {
		const activeRemixId = remixIdRef.current;
		if (!activeRemixId || finalizedRef.current) {
			return undefined;
		}

		// Detach the claimed flow before invoking any external callback. A command handler reached
		// through `sendRemixEvent` or `endRemix` may synchronously start another flow, and finalizing
		// this flow must not clear that new owner's identity or cancellation session.
		const cancellationSessionId = cancellationSessionIdRef.current;
		finalizedRef.current = true;
		remixIdRef.current = undefined;
		cancellationSessionIdRef.current = undefined;
		pendingIntentRef.current = undefined;
		return { remixId: activeRemixId, cancellationSessionId };
	}, []);

	const releaseClaimedFlow = useCallback((finalization: ClaimedFinalization) => {
		if (commandRemixIdRef.current === finalization.remixId) {
			commandRemixIdRef.current = undefined;
		}
		if (finalization.cancellationSessionId) {
			unregisterRemixCancellationSession(finalization.cancellationSessionId);
		}
	}, []);
	const safelyEndRemix = useCallback(
		(id: string, reason: RemixExplicitFlowEndReason) => {
			runLifecycleSafely('endRemix', () => endRemix(id, reason));
		},
		[runLifecycleSafely],
	);
	const endObservedTerminal = useCallback(
		(reason: RemixExplicitFlowEndReason) => {
			const finalization = claimFinalization();
			if (!finalization) {
				return;
			}
			try {
				safelyEndRemix(finalization.remixId, reason);
			} finally {
				releaseClaimedFlow(finalization);
			}
		},
		[claimFinalization, releaseClaimedFlow, safelyEndRemix],
	);

	const endWith = useCallback(
		({
			event,
			reason,
		}: {
			event: 'GENERATION_ABORTED' | 'GENERATION_ERROR' | 'CANCELLED';
			reason: RemixExplicitFlowEndReason;
		}): boolean => {
			const finalization = claimFinalization();
			if (!finalization) {
				return false;
			}
			const activeRemixId = finalization.remixId;
			let terminalPhaseBeforeSend: RendererRemixTerminalPhase | undefined;
			let didApplyTerminalEvent = false;

			try {
				terminalPhaseBeforeSend = readMachineTerminalPhase(activeRemixId);
				runLifecycleSafely('sendTerminalEvent', () =>
					sendRemixEvent(activeRemixId, { type: event }),
				);
				const terminalPhase = readMachineTerminalPhase(activeRemixId);
				didApplyTerminalEvent =
					terminalPhaseBeforeSend === undefined && terminalPhase !== undefined;
				safelyEndRemix(activeRemixId, reason);
			} finally {
				releaseClaimedFlow(finalization);
			}

			return didApplyTerminalEvent;
		},
		[claimFinalization, releaseClaimedFlow, runLifecycleSafely, safelyEndRemix],
	);

	const startFlow = useCallback(
		({
			conversationId: requestedConversationId,
			dynamicUiType,
			dynamicUiSubtype,
			source,
		}: StartRendererRemixArgs): RemixAdmission => {
			const conversationId = requestedConversationId ?? uuidv4();

			// Preserve any existing admitted flow. SINGLE_ACTIVE_POLICY owns rejection.
			if (remixIdRef.current && !finalizedRef.current) {
				runLifecycleSafely('notifyAdmissionRejected', () => onAdmissionRejected?.('active-flow'));
				return { conversationId, admitted: false, reason: 'active-flow' };
			}

			const cancellationSessionId = registerRemixCancellationSession({ conversationId });
			if (!cancellationSessionId) {
				runLifecycleSafely('notifyAdmissionRejected', () => onAdmissionRejected?.('active-flow'));
				return { conversationId, admitted: false, reason: 'active-flow' };
			}

			try {
				const result = startRemix({
					kind: 'create',
					conversationId,
					cancellationSessionId,
					metadata: { dynamicUiType, dynamicUiSubtype, source },
					useCommandBus: true,
				});

				if (!result) {
					unregisterRemixCancellationSession(cancellationSessionId);
					runLifecycleSafely('notifyAdmissionRejected', () => onAdmissionRejected?.('active-flow'));
					return { conversationId, admitted: false, reason: 'active-flow' };
				}

				if (result.conversationId !== conversationId) {
					unregisterRemixCancellationSession(cancellationSessionId);
					logLifecycleOperationError(
						'validateAdmissionIdentity',
						new Error('Machine admission returned a different conversation identity'),
					);
					runLifecycleSafely('notifyAdmissionRejected', () =>
						onAdmissionRejected?.('identity-mismatch'),
					);
					safelyEndRemix(result.remixId, 'cancelled');
					return { conversationId, admitted: false, reason: 'identity-mismatch' };
				}

				finalizedRef.current = false;
				remixIdRef.current = result.remixId;
				commandRemixIdRef.current = result.remixId;
				cancellationSessionIdRef.current = cancellationSessionId;
				pendingIntentRef.current = undefined;
				setRemixId(result.remixId);
				return { conversationId, admitted: true, cancellationSessionId };
			} catch (error) {
				unregisterRemixCancellationSession(cancellationSessionId);
				logLifecycleOperationError('startRemix', error);
				runLifecycleSafely('notifyAdmissionRejected', () => onAdmissionRejected?.('error'));
				return { conversationId, admitted: false, reason: 'error' };
			}
		},
		[logLifecycleOperationError, onAdmissionRejected, runLifecycleSafely, safelyEndRemix],
	);

	const reconcileIntent = useCallback(
		(intent: Exclude<PendingIntent, undefined>) => {
			const activeRemixId = remixIdRef.current;
			if (!activeRemixId || finalizedRef.current) {
				return;
			}

			const currentPhase = phaseRef.current;
			if (intent === 'preview-opened' && currentPhase === 'ready') {
				pendingIntentRef.current = undefined;
				if (
					!runLifecycleSafely('sendPreviewOpened', () =>
						sendRemixEvent(activeRemixId, { type: 'PREVIEW_OPENED' }),
					)
				) {
					const finalization = claimFinalization();
					if (finalization) {
						try {
							safelyEndRemix(finalization.remixId, 'cancelled');
						} finally {
							releaseClaimedFlow(finalization);
						}
					}
				}
				return;
			}
		},
		[claimFinalization, releaseClaimedFlow, runLifecycleSafely, safelyEndRemix],
	);

	const onPreviewOpened = useCallback(() => {
		if (remixIdRef.current && !finalizedRef.current) {
			pendingIntentRef.current = 'preview-opened';
			reconcileIntent('preview-opened');
		}
	}, [reconcileIntent]);

	const onPreviewDismissed = useCallback(() => {
		return endWith({ event: 'CANCELLED', reason: 'cancelled' });
	}, [endWith]);

	const onStopThinking = useCallback(() => {
		const activeRemixId = remixIdRef.current;
		const currentPhase = activeRemixId ? getRemixMachine(activeRemixId)?.state() : undefined;
		return endWith({
			event: currentPhase === 'thinking' ? 'GENERATION_ABORTED' : 'CANCELLED',
			reason: 'aborted',
		});
	}, [endWith]);

	const onRovoAborted = useCallback(() => {
		endObservedTerminal('aborted');
	}, [endObservedTerminal]);

	const onErrorDismissed = useCallback(() => {
		endObservedTerminal('cancelled');
	}, [endObservedTerminal]);

	const onSendRejected = useCallback(() => {
		return endWith({ event: 'GENERATION_ERROR', reason: 'error' });
	}, [endWith]);

	const onNavigation = useCallback(() => {
		return endWith({ event: 'CANCELLED', reason: 'cancelled' });
	}, [endWith]);

	useEffect(() => {
		const pendingIntent = pendingIntentRef.current;
		if (!pendingIntent) {
			return;
		}

		// Reconcile the queued renderer intent only after the watcher exposes a legal machine phase.
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		reconcileIntent(pendingIntent);
	}, [phase, reconcileIntent]);

	// Component teardown cancels the flow and releases machine admission.
	useEffect(() => {
		return () => {
			const finalization = claimFinalization();
			if (!finalization) {
				return;
			}
			const activeRemixId = finalization.remixId;
			try {
				runLifecycleSafely('sendUnmountCancelled', () =>
					sendRemixEvent(activeRemixId, { type: 'CANCELLED' }),
				);
				safelyEndRemix(activeRemixId, 'cancelled');
			} finally {
				releaseClaimedFlow(finalization);
			}
		};
	}, [claimFinalization, releaseClaimedFlow, runLifecycleSafely, safelyEndRemix]);

	const isAdmittedRemixId = useCallback((id: string) => commandRemixIdRef.current === id, []);
	return {
		phase,
		remixId,
		isAdmittedRemixId,
		startFlow,
		onPreviewOpened,
		onPreviewDismissed,
		onStopThinking,
		onRovoAborted,
		onErrorDismissed,
		onSendRejected,
		onNavigation,
	};
};
